const blobToDataUrl = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onloadend = () => resolve(String(reader.result || ""));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

const waitForImageToLoad = (image: HTMLImageElement): Promise<void> => new Promise((resolve) => {
  if (!image.src || image.complete) {
    resolve();
    return;
  }

  const timeout = window.setTimeout(resolve, 5000);
  const done = () => {
    window.clearTimeout(timeout);
    resolve();
  };

  image.addEventListener("load", done, { once: true });
  image.addEventListener("error", done, { once: true });
});

const getImageFetchCredentials = (imageUrl: string): "include" | "omit" => {
  try {
    const url = new URL(imageUrl, window.location.href);
    return url.origin === window.location.origin ? "include" : "omit";
  } catch {
    return "include";
  }
};

interface WaitForImagesToLoadParams {
  container: Element;
}

interface GetPdfReadyImageSourceParams {
  imageUrl?: string | null;
  fetchImage?: typeof globalThis.fetch;
}

/**
 * Waits for images inside a report page before html2canvas snapshots it.
 * html2canvas can otherwise capture an empty image box if the DOM is ready
 * before the browser has finished decoding remote logos/profile images.
 */
export const waitForImagesToLoad = async ({ container }: WaitForImagesToLoadParams): Promise<void> => {
  const images = Array.from(container.querySelectorAll("img"));
  await Promise.all(images.map(waitForImageToLoad));
};

/**
 * Converts a remotely fetched image into a data URL for PDF rendering.
 * Browser display can load cross-origin images that canvas/PDF export cannot
 * safely read, so this embeds readable logo bytes before html2canvas runs.
 * If the browser cannot fetch the image bytes, the original URL is returned
 * so normal on-page rendering still works.
 */
export const getPdfReadyImageSource = async ({
  imageUrl,
  fetchImage,
}: GetPdfReadyImageSourceParams): Promise<string> => {
  if (!imageUrl) {
    return "";
  }

  if (imageUrl.startsWith("data:")) {
    return imageUrl;
  }

  const imageFetcher = fetchImage || globalThis.fetch;
  if (!imageFetcher) {
    return imageUrl;
  }

  try {
    const response = await imageFetcher(imageUrl, {
      credentials: getImageFetchCredentials(imageUrl),
      mode: "cors",
    });

    if (!response.ok) {
      return imageUrl;
    }

    return await blobToDataUrl(await response.blob());
  } catch {
    return imageUrl;
  }
};
